import { Box, Flex } from "@mantine/core";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotifications } from "@/context/NotificationContext";
import DiscoverExploreTabs from "@/pages/discover/components/DiscoverExploreTabs";
import {
  CLIENT_EXPLORE_PATH,
  EXPLORE_TAB_SWITCH_STATE,
  exploreTabHref,
  resolveExploreTab,
  type ExploreTab,
} from "@/pages/discover/discoverExplore.const";
import { ExpoVisitorHeaderCopy } from "@/components/layout/ExpoVisitorHeaderCopy";
import { getExpoVisitorRouteHeader } from "@/components/layout/expoVisitorHeader.routes";
import AppHeaderUserActions from "@/components/shared/AppHeaderUserActions";
import { useUser } from "@/context/UserContext";
import { isExpoSignupUser } from "@/shared/constants/clientOnboarding.const";

const HEADER_GAP_PX = 12;
/** Extra slack so we collapse slightly before a hard squeeze. */
const FIT_SLACK_PX = 8;
/** Fallback until the first inline icon measurement lands. */
const ICONS_WIDTH_FALLBACK_PX = 220;

/** Mobile auth header: second row under logo/burger with the explore pills. */
export const AUTH_MOBILE_EXPLORE_ROW_HEIGHT = 52;

/** Unread cluster updates surface on the "Clusters and Corridors" pill. */
function useExploreTabCounts(): Partial<Record<ExploreTab, number>> {
  const { corridorUnread } = useNotifications();
  return useMemo(() => ({ corridors: corridorUnread.total }), [corridorUnread.total]);
}

export function AuthExploreMobileTabsRow() {
  const navigate = useNavigate();
  const location = useLocation();
  const tabCounts = useExploreTabCounts();
  const activeTab: ExploreTab = resolveExploreTab(location.pathname, location.search) ?? "companies";

  const handleTabChange = useCallback(
    (tab: ExploreTab) => {
      navigate(exploreTabHref(CLIENT_EXPLORE_PATH, tab), { state: EXPLORE_TAB_SWITCH_STATE });
    },
    [navigate],
  );

  return (
    <Flex
      h={AUTH_MOBILE_EXPLORE_ROW_HEIGHT}
      px="md"
      align="center"
      style={{ borderTop: "1px solid #eef2f7", minWidth: 0, width: "100%" }}
    >
      <Box w="100%" miw={0} style={{ height: 44, display: "flex", alignItems: "center", minWidth: 0 }}>
        <DiscoverExploreTabs
          placement="header"
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabCounts={tabCounts}
        />
      </Box>
    </Flex>
  );
}

/**
 * Desktop/tablet auth top bar: explore pills + space-aware user actions.
 * When pills + full icons do not fit, icons collapse behind a leftmost ⋯ control.
 */
export default function AuthExploreTopHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useUser();
  const hideExploreTabs = isExpoSignupUser(userDetails);
  const visitorHeader = hideExploreTabs ? getExpoVisitorRouteHeader(location.pathname) : {};
  const rowRef = useRef<HTMLDivElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);
  const iconsRef = useRef<HTMLDivElement>(null);
  const iconsWidthRef = useRef(ICONS_WIDTH_FALLBACK_PX);
  const iconsCollapsedRef = useRef(false);
  const [iconsCollapsed, setIconsCollapsed] = useState(false);
  const tabCounts = useExploreTabCounts();

  const activeTab: ExploreTab = resolveExploreTab(location.pathname, location.search) ?? "companies";

  const handleTabChange = useCallback(
    (tab: ExploreTab) => {
      navigate(exploreTabHref(CLIENT_EXPLORE_PATH, tab));
    },
    [navigate],
  );

  const measure = useCallback(() => {
    const row = rowRef.current;
    const pills = pillsRef.current;
    if (!row || !pills) return;

    const available = row.clientWidth;
    // Prefer natural tab label widths so we collapse icons before truncating pills.
    const tabEls = pills.querySelectorAll("[data-explore-tab]");
    let pillsNaturalWidth = 0;
    if (tabEls.length > 0) {
      tabEls.forEach((el) => {
        pillsNaturalWidth += (el as HTMLElement).scrollWidth;
      });
      pillsNaturalWidth += 8; // track padding
    } else {
      pillsNaturalWidth = pills.scrollWidth;
    }

    const iconsEl = iconsRef.current;
    if (!iconsCollapsedRef.current && iconsEl && iconsEl.offsetWidth > 0) {
      iconsWidthRef.current = iconsEl.offsetWidth;
    }

    const needsCollapse =
      pillsNaturalWidth + iconsWidthRef.current + HEADER_GAP_PX + FIT_SLACK_PX > available;
    iconsCollapsedRef.current = needsCollapse;
    setIconsCollapsed((prev) => (prev === needsCollapse ? prev : needsCollapse));
  }, []);

  useLayoutEffect(() => {
    measure();
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(row);
    if (pillsRef.current) ro.observe(pillsRef.current);
    return () => ro.disconnect();
  }, [measure, activeTab, visitorHeader.title, visitorHeader.description]);

  return (
    <Flex
      ref={rowRef}
      h="100%"
      pl={{ base: "sm", sm: "md" }}
      pr={{ base: "sm", sm: "md" }}
      align="center"
      gap={HEADER_GAP_PX}
      wrap="nowrap"
      style={{ minWidth: 0, width: "100%", position: "relative" }}
    >
      <Box
        ref={pillsRef}
        miw={0}
        style={{ flex: "1 1 auto", maxWidth: "100%", minWidth: 0, width: "100%" }}
        data-discover-tour={hideExploreTabs ? undefined : "tabs"}
      >
        {hideExploreTabs ? (
          visitorHeader.title ? (
            <ExpoVisitorHeaderCopy title={visitorHeader.title} description={visitorHeader.description} />
          ) : null
        ) : (
          <DiscoverExploreTabs
            placement="header"
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabCounts={tabCounts}
          />
        )}
      </Box>

      <Box ref={iconsRef} style={{ flexShrink: 0 }}>
        <AppHeaderUserActions mode={iconsCollapsed ? "collapsed" : "inline"} />
      </Box>
    </Flex>
  );
}
